/**
 * aiProgress.js — adapter between the AI games and the EXISTING mastery
 * engine (masteryEngine.js). No parallel progress store: AI objectives are
 * ordinary mastery skills, so AI learning shows up in the same places all
 * other mastery does. (Universal AI Learning System §11 mastery, §3 model.)
 */
import { recordAttempt, loadSkillProgress, isMasteredNow } from "@/lib/masteryEngine";
import { AI_OBJECTIVES, OBJECTIVE_BY_ID, objectivesForGame } from "@/data/aiCurriculum";

// §11 — six evidence-based mastery levels, mapped from the engine's 0–100
// masteryScore (which moves on correct/incorrect attempts, not on "viewed").
export const STATUS = {
  NOT_INTRODUCED: { key: "NOT_INTRODUCED", label: "New", short: "New", tone: "slate" },
  INTRODUCED: { key: "INTRODUCED", label: "Started", short: "Started", tone: "sky" },
  PRACTISING: { key: "PRACTISING", label: "Practising", short: "Practising", tone: "violet" },
  DEVELOPING: { key: "DEVELOPING", label: "Growing", short: "Growing", tone: "amber" },
  SECURE: { key: "SECURE", label: "Strong", short: "Strong", tone: "emerald" },
  MASTERED: { key: "MASTERED", label: "Mastered", short: "Mastered", tone: "emerald" },
};

function statusFromRow(row, skill) {
  const s = row.masteryScore || 0;
  if (isMasteredNow(skill)) return STATUS.MASTERED;
  if (s <= 0) return STATUS.NOT_INTRODUCED;
  if (s < 25) return STATUS.INTRODUCED;
  if (s < 50) return STATUS.PRACTISING;
  if (s < 75) return STATUS.DEVELOPING;
  return STATUS.SECURE;
}

export function objectiveStatus(objId) {
  const obj = OBJECTIVE_BY_ID[objId];
  if (!obj) return STATUS.NOT_INTRODUCED;
  const row = loadSkillProgress(obj.skill);
  return statusFromRow(row, obj.skill);
}

export function objectiveScore(objId) {
  const obj = OBJECTIVE_BY_ID[objId];
  if (!obj) return 0;
  return loadSkillProgress(obj.skill).masteryScore || 0;
}

/**
 * Record real evidence for one or more objectives after an activity.
 * Feeds the existing mastery engine (which also grants XP + fires the
 * parent-dashboard "quiz" event on a correct attempt).
 */
export function recordAiEvidence(objectiveIds = [], correct = true) {
  objectiveIds.forEach((id) => {
    const obj = OBJECTIVE_BY_ID[id];
    if (!obj) return;
    const firstTry = (loadSkillProgress(obj.skill).attempts || 0) === 0;
    recordAttempt(obj.skill, { correct, firstTry, strand: "ai" });
  });
}

/** Aggregate status for a whole game (average of its objectives' scores). */
export function gameProgress(gameId) {
  const objs = objectivesForGame(gameId);
  if (!objs.length) return { pct: 0, status: STATUS.NOT_INTRODUCED, mastered: 0, total: 0 };
  const scores = objs.map((o) => objectiveScore(o.id));
  const pct = Math.round(scores.reduce((a, b) => a + b, 0) / objs.length);
  const mastered = objs.filter((o) => isMasteredNow(o.skill)).length;
  let status = STATUS.NOT_INTRODUCED;
  if (mastered === objs.length) status = STATUS.MASTERED;
  else if (pct <= 0) status = STATUS.NOT_INTRODUCED;
  else if (pct < 25) status = STATUS.INTRODUCED;
  else if (pct < 50) status = STATUS.PRACTISING;
  else if (pct < 75) status = STATUS.DEVELOPING;
  else status = STATUS.SECURE;
  return { pct, status, mastered, total: objs.length };
}

/** Whole-pathway overview for the AI Island map header. */
export function aiOverview() {
  const total = AI_OBJECTIVES.length;
  const mastered = AI_OBJECTIVES.filter((o) => isMasteredNow(o.skill)).length;
  const started = AI_OBJECTIVES.filter((o) => (objectiveScore(o.id) > 0)).length;
  const avg = Math.round(
    AI_OBJECTIVES.reduce((a, o) => a + objectiveScore(o.id), 0) / total,
  );
  return { total, mastered, started, pct: avg };
}

/** Are a game's EXTERNAL prerequisite objectives at least started? (§12) */
export function gamePrereqsMet(gameId) {
  const objs = objectivesForGame(gameId);
  const prereqIds = [...new Set(objs.flatMap((o) => o.prerequisiteIds || []))]
    // Ignore prerequisites that belong to this same game (intra-game chains).
    .filter((pid) => !(OBJECTIVE_BY_ID[pid]?.relatedGameIds || []).includes(gameId));
  return prereqIds.every((pid) => objectiveScore(pid) > 0);
}

/**
 * §12 — recommend the next best AI activity from an ordered list of game
 * keys: the lowest-progress, not-yet-mastered game whose prerequisites are
 * met (falling back to the first non-mastered game). Returns {key, reason}
 * or null when everything is mastered.
 */
export function recommendNext(orderedGameIds = []) {
  const rows = orderedGameIds
    .map((key, order) => ({ key, order, prog: gameProgress(key), met: gamePrereqsMet(key) }))
    .filter((r) => r.prog.status.key !== "MASTERED");
  if (!rows.length) return null;
  const eligible = rows.filter((r) => r.met);
  const pool = eligible.length ? eligible : rows;
  pool.sort((a, b) => (a.prog.pct - b.prog.pct) || (a.order - b.order));
  const pick = pool[0];
  const reason = pick.prog.pct === 0 ? "A new adventure to start" : "Pick up where you left off";
  return { key: pick.key, reason };
}
